'use client'

import { useEffect, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import qs from 'qs'

import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import BackToTopButton from 'shared/ui/BackToTopButton'
import SelectDropdown from 'shared/ui/form/Select'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { viewProductList } from '../analytics'
import { getNextUpsellingPage } from '../api/products'
import ProductCard from '../components/ProductCard'
import useInfiniteScroll from '../hooks/useInfiniteScroll'
import { ProductMetaTypes, ProductTypes } from '../types/products'
import { parseQueryFilters } from '../utils/filters'
import {
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValueForUpselling,
  PROMOTED_SORTING_OPTIONS
} from '../utils/sorting'

import styles from './PromotionsLegacyPage.module.scss'

const PROMOTIONS_LIST_NAME = SeoListName.PROMOTIONS_LIST_NAME

interface PromocjeLegacyClientProps {
  products: ProductTypes[]
  meta: ProductMetaTypes
  queryParams: { [key: string]: string | string[] | undefined }
  token: string
}

export default function PromotionsLegacyPage({
  products,
  token,
  queryParams
}: PromocjeLegacyClientProps) {
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const [sorting, setSorting] = useState<string | string[]>('recommended')
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const { productsData, ref, isFetching } = useInfiniteScroll({
    products,
    token,
    query: queryParams,
    getNextPageCallback: getNextUpsellingPage,
    limit: 30
  })

  // Convert searchParams to query object
  const query = Object.fromEntries(searchParams?.entries() || [])

  const handleSortingChange = async (_, value: string | string[]) => {
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValueForUpselling(value)
    const parsedQuery = parseQueryFilters(query)

    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...parsedQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.push(`${pathname}${decodeURIComponent(params)}`)
  }

  useEffect(() => {
    const sortingToCheck = {
      sort_by: query.sort_by,
      order: query.order
    }

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
  }, [query, sorting])

  useEffect(() => {
    if (session?.status !== 'loading' && isNotNilOrEmpty(productsData)) {
      viewProductList({
        user,
        products: productsData || [],
        listName: PROMOTIONS_LIST_NAME
      })
    }
  }, [session, productsData, user])

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>Promocje</h1>
        <div className={styles.sortingInputWrapper}>
          <div className={styles.sortingLabel}>Sortuj:</div>
          <SelectDropdown
            id='promotions-legacy-page-sorting'
            variant='ghost'
            noMargin
            options={PROMOTED_SORTING_OPTIONS}
            name='sorting'
            value={sorting}
            onChange={handleSortingChange}
          />
        </div>
      </div>
      <span data-title='top' />
      <div className={styles.wrapper}>
        <BackToTopButton target='*[data-title="top"]' />
        {productsData &&
          productsData.map((product: ProductTypes, index) => {
            return (
              <ProductCard
                key={`${product.id}${index}`}
                product={product}
                productPath={product.slug}
                seoButtonPlace={pathname || ''}
                listName={PROMOTIONS_LIST_NAME}
                idxOnList={index}
                id={`promotions_page_product_${product.id}`}
              />
            )
          })}
        <div ref={ref}></div>
      </div>
      {isFetching && (
        <div className={styles.loaderWrapper}>
          <LoadingIndicator />
        </div>
      )}
    </>
  )
}
